import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import returnYear from "../utils/GetYear";

function NavBar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedSubmenus, setExpandedSubmenus] = useState({
    paintings: false,
    shop: false,
  });
  const location = useLocation();

  const closeMenu = () => setIsMenuOpen(false);
  const toggleMenu = () => setIsMenuOpen((open) => !open);
  const toggleSubmenu = (key) =>
    setExpandedSubmenus((prev) => ({ ...prev, [key]: !prev[key] }));

  // Close the mobile menu whenever the route changes
  useEffect(() => {
    setIsMenuOpen(false);
    // The desktop dropdown is shown via :focus-within on the wrapper, so a
    // just-clicked dropdown <Link> keeps it open even after navigating to a
    // new page. Drop focus on route change so the dropdown collapses; if the
    // cursor has also moved off the wrapper, :hover no longer holds it open.
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
  }, [location.pathname]);

  // Reset submenu accordions whenever the main menu closes
  useEffect(() => {
    if (!isMenuOpen) {
      setExpandedSubmenus({ paintings: false, shop: false });
    }
  }, [isMenuOpen]);

  // Lock body scroll + listen for Esc when menu is open
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  // Close menu if viewport widens past the mobile breakpoint
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 810) setIsMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reusable chevron SVG
  const Chevron = () => (
    <svg
      className='navBarMobileChevron'
      viewBox='0 0 24 24'
      width='14'
      height='14'
      aria-hidden='true'
    >
      <path
        d='M6 9l6 6 6-6'
        stroke='currentColor'
        strokeWidth='2.2'
        fill='none'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );

  return (
    <div className='navBarContainer'>
      <nav className='navBar'>
        <Link className='navBarLink' to='/'>
          Home
        </Link>
        <div className='navBarDropdownWrapper'>
          <Link className='navBarLink' to='/paintings'>
            Paintings
          </Link>
          <div className='navBarDropdown'>
            <Link className='navBarDropdownLink' to='/paintings/peoplescapes-new'>
              {`Peoplescapes 2024 - ${returnYear()}`}
            </Link>
            <Link className='navBarDropdownLink' to='/paintings/timemachine-2025'>
              Time Machine 2025
            </Link>
            <Link className='navBarDropdownLink' to='/paintings/peoplescapes-2023'>
              Peoplescapes 2018 - 2023
            </Link>
            <Link className='navBarDropdownLink' to='/paintings/peoplescapes-old'>
              Peoplescapes 2007 - 2017
            </Link>
            <Link className='navBarDropdownLink' to='/paintings/abstracts'>
              Abstracts
            </Link>
            <Link className='navBarDropdownLink' to='/paintings/cityscapes'>
              Cityscapes
            </Link>
            <Link className='navBarDropdownLink' to='/paintings/interiors'>
              Interiors
            </Link>
            <Link className='navBarDropdownLink' to='/paintings/misc'>
              Miscellaneous
            </Link>
          </div>
        </div>
        <Link className='navBarLink' to='/biography'>
          About
        </Link>
        <div className='navBarDropdownWrapper'>
          <Link className='navBarLink' to='/shop'>
            Shop
          </Link>
          <div className='navBarDropdown'>
            <Link className='navBarDropdownLink' to='/prints'>
              Limited Edition Prints
            </Link>
            <Link className='navBarDropdownLink' to='/painting-shop'>
              Original Paintings
            </Link>
          </div>
        </div>
      </nav>

      <button
        className={`navBarHamburger ${isMenuOpen ? "isOpen" : ""}`}
        onClick={toggleMenu}
        aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        aria-expanded={isMenuOpen}
        aria-controls='mobileNavMenu'
      >
        <span className='navBarHamburgerBar' />
        <span className='navBarHamburgerBar' />
        <span className='navBarHamburgerBar' />
      </button>

      <div
        id='mobileNavMenu'
        className={`navBarMobileMenu ${isMenuOpen ? "isOpen" : ""}`}
        aria-hidden={!isMenuOpen}
      >
        <Link className='navBarMobileLink' to='/' onClick={closeMenu}>
          Home
        </Link>

        <div className='navBarMobileRow'>
          <Link className='navBarMobileLink hasSubmenu' to='/paintings' onClick={closeMenu}>
            Paintings
          </Link>
          <button
            type='button'
            className={`navBarMobileToggle ${expandedSubmenus.paintings ? "isOpen" : ""}`}
            onClick={() => toggleSubmenu("paintings")}
            aria-label={expandedSubmenus.paintings ? "Collapse Paintings submenu" : "Expand Paintings submenu"}
            aria-expanded={expandedSubmenus.paintings}
            aria-controls='mobilePaintingsSubmenu'
          >
            <Chevron />
          </button>
        </div>
        <div
          id='mobilePaintingsSubmenu'
          className={`navBarMobileSubgroup ${expandedSubmenus.paintings ? "isOpen" : ""}`}
        >
          <Link className='navBarMobileSubLink' to='/paintings/peoplescapes-new' onClick={closeMenu}>
            {`Peoplescapes 2024 - ${returnYear()}`}
          </Link>
          <Link className='navBarMobileSubLink' to='/paintings/timemachine-2025' onClick={closeMenu}>
            Time Machine 2025
          </Link>
          <Link className='navBarMobileSubLink' to='/paintings/peoplescapes-2023' onClick={closeMenu}>
            Peoplescapes 2018 - 2023
          </Link>
          <Link className='navBarMobileSubLink' to='/paintings/peoplescapes-old' onClick={closeMenu}>
            Peoplescapes 2007 - 2017
          </Link>
          <Link className='navBarMobileSubLink' to='/paintings/abstracts' onClick={closeMenu}>
            Abstracts
          </Link>
          <Link className='navBarMobileSubLink' to='/paintings/cityscapes' onClick={closeMenu}>
            Cityscapes
          </Link>
          <Link className='navBarMobileSubLink' to='/paintings/interiors' onClick={closeMenu}>
            Interiors
          </Link>
          <Link className='navBarMobileSubLink' to='/paintings/misc' onClick={closeMenu}>
            Miscellaneous
          </Link>
        </div>

        <Link className='navBarMobileLink' to='/biography' onClick={closeMenu}>
          About
        </Link>

        <div className='navBarMobileRow'>
          <Link className='navBarMobileLink hasSubmenu' to='/shop' onClick={closeMenu}>
            Shop
          </Link>
          <button
            type='button'
            className={`navBarMobileToggle ${expandedSubmenus.shop ? "isOpen" : ""}`}
            onClick={() => toggleSubmenu("shop")}
            aria-label={expandedSubmenus.shop ? "Collapse Shop submenu" : "Expand Shop submenu"}
            aria-expanded={expandedSubmenus.shop}
            aria-controls='mobileShopSubmenu'
          >
            <Chevron />
          </button>
        </div>
        <div
          id='mobileShopSubmenu'
          className={`navBarMobileSubgroup ${expandedSubmenus.shop ? "isOpen" : ""}`}
        >
          <Link className='navBarMobileSubLink' to='/prints' onClick={closeMenu}>
            Limited Edition Prints
          </Link>
          <Link className='navBarMobileSubLink' to='/painting-shop' onClick={closeMenu}>
            Original Paintings
          </Link>
        </div>
      </div>
    </div>
  );
}

export default NavBar;
